import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import '@fontsource/red-hat-display';
import './App.css';
import logo from './logo-g.svg';
import { Analytics } from "@vercel/analytics/react";

/* ========== Constants ========== */
const CHART_COLORS = ['#f72585', '#b5179e', '#7209b7', '#480ca8', '#4895ef', '#3f37c9', '#4cc9f0', '#f72585'];

const SHIPPING_SIZE_LABELS = [
  { key: 'small', label: '35x25x8cm < 1 kg' },
  { key: 'medium', label: '120x60x60cm < 4 kg' },
  { key: 'large', label: '120x60x60cm > 4 kg' }
];

const DEFAULT_SHIPPING_OPTIONS = [
  { label: SHIPPING_SIZE_LABELS[0].label, value: 3.35 },
  { label: SHIPPING_SIZE_LABELS[1].label, value: 3.90 },
  { label: SHIPPING_SIZE_LABELS[2].label, value: 4.35 }
];

const ADVERTISING_OPTIONS = [0, 5, 10, 15, 20, 25, 30];
const RETURNS_OPTIONS = Array.from({ length: 21 }, (_, i) => i);
const COUNTRIES = ['DE', 'UK', 'FR', 'PL', 'NL', 'ES', 'IT', 'PT', 'NO', 'AT'];

const range = (min, max) => {
  const a = [];
  for (let i = min; i <= max; i++) a.push(i);
  return a;
};

const INFO_TEXTS = {
  product: "Enter your product's market price, estimated monthly sales quantity, and cost of goods sold (COGS) to calculate profitability. The system will compute all fees and margins automatically.",
  fees: "Configure marketplace fees, VAT, grow commissions, advertising spend, return rates, and fulfillment costs. These percentages and fixed costs will be applied to your product calculations. Country selection updates fulfillment rates automatically.",
  analysis: "Visual breakdown of all costs including marketplace fees, VAT, grow fees, advertising, COGS, and fulfillment costs. The pie chart shows cost distribution, while the fulfillment section details all logistics fees that can be toggled on/off for manual override.",
  vendor: "Compare your 3P marketplace model with a 1P vendor scenario. Enter the net sales price you'd receive from a vendor like Amazon, and see how various vendor fees impact your profit margin.",
  addPortfolio: "Save your calculated product with all current fee settings to your portfolio. Give it a custom name and press Enter or click Add. This allows you to compare multiple products and see combined projections.",
  portfolio: "Your saved products with calculations. Adjust quantities, edit products, or export to Excel. The chart shows cumulative quarterly projections."
};

const COUNTRY_RATES = {
  DE: {
    shippingSizes: [3.35, 3.59, 4.40],
    packagingSizes: [0.40, 0.85, 1.05],
    orderFee: 0.80, pickPerSku: 0.26, pickPerPiece: 0.08, inbound: 0.10, storage: 0.10,
    packagingLabel: 0.20
  },
  UK: {
    shippingSizes: [4.20, 5.00, 6.50],
    packagingSizes: [0.25, 0.75, 0.95],
    orderFee: 1.40, pickPerSku: 0.15, pickPerPiece: 0.09, inbound: 0.15, storage: 0.15,
    packagingLabel: 0.40
  },
  FR: {
    shippingSizes: [5.80, 6.50, 7.80],
    packagingSizes: [0.20, 0.65, 0.85],
    orderFee: 0.90, pickPerSku: 0.28, pickPerPiece: 0.09, inbound: 0.06, storage: 0.05,
    packagingLabel: 0.22
  },
  PL: {
    shippingSizes: [3.05, 3.80, 4.00],
    packagingSizes: [0.15, 0.65, 0.75],
    orderFee: 0.80, pickPerSku: 0.26, pickPerPiece: 0.08, inbound: 0.05, storage: 0.05,
    packagingLabel: 0.20
  },
  NL: {
    shippingSizes: [3.34, 4.00, 4.28],
    packagingSizes: [0.20, 0.50, 0.80],
    orderFee: 1.52, pickPerSku: 0.43, pickPerPiece: 0.14, inbound: 0.10, storage: 0.10,
    packagingLabel: 0.35
  },
  ES: {
    shippingSizes: [3.90, 4.60, 5.70],
    packagingSizes: [0.20, 0.65, 0.85],
    orderFee: 0.88, pickPerSku: 0.27, pickPerPiece: 0.085, inbound: 0.03, storage: 0.03,
    packagingLabel: 0.22
  },
  IT: {
    shippingSizes: [4.00, 4.80, 5.80],
    packagingSizes: [0.20, 0.65, 0.85],
    orderFee: 0.92, pickPerSku: 0.29, pickPerPiece: 0.09, inbound: 0.03, storage: 0.03,
    packagingLabel: 0.24
  },
  PT: {
    shippingSizes: [4.10, 4.90, 6.00],
    packagingSizes: [0.22, 0.60, 0.82],
    orderFee: 0.95, pickPerSku: 0.30, pickPerPiece: 0.09, inbound: 0.03, storage: 0.04,
    packagingLabel: 0.25
  },
  NO: {
    shippingSizes: [6.50, 7.50, 9.00],
    packagingSizes: [0.50, 1.40, 1.90],
    orderFee: 1.80, pickPerSku: 0.35, pickPerPiece: 0.21, inbound: 0.15, storage: 0.15,
    packagingLabel: 0.40
  },
  AT: {
    shippingSizes: [3.90, 4.17, 4.70],
    packagingSizes: [0.20, 0.65, 0.85],
    orderFee: 0.80, pickPerSku: 0.26, pickPerPiece: 0.08, inbound: 0.05, storage: 0.05,
    packagingLabel: 0.20
  }
};

/* ========== Helper Functions ========== */
function toNumberSafe(value, fallback = 0) {
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const formatCurrency = (amount) => 
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(amount || 0);

const formatEuNoDecimals = (amount) => 
  new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(amount || 0)) + '€';

function shortenK(n) {
  if (Math.abs(n) >= 1000000) {
    const val = n / 1000000;
    return (Math.round(val * 10) / 10).toString().replace(/\.0$/, '') + 'M';
  }
  if (Math.abs(n) >= 1000) {
    const val = n / 1000;
    const rounded = Math.round(val * 10) / 10;
    return (rounded.toString().replace(/\.0$/, '') + 'k').toLowerCase();
  }
  return String(Math.round(n));
}

/* ========== Calculation Functions ========== */
function computeCalculation(product, fees, priceMode = 'net') {
  if (!product) return null;
  const grossPrice = toNumberSafe(product.price, 0);

  const marketplacePct = toNumberSafe(fees.marketplace, 0);
  const vatPct = toNumberSafe(fees.vat, 0);
  const brokerPct = toNumberSafe(fees.broker, 0);
  const advertisingPct = toNumberSafe(fees.advertising, 0);
  const returnsPct = toNumberSafe(fees.returns, 0);

  const orderFee = toNumberSafe(fees.orderFee, 0);
  const pickPerSku = toNumberSafe(fees.pickPerSku, 0);
  const pickPerPiece = toNumberSafe(fees.pickPerPiece, 0);
  const inbound = toNumberSafe(fees.inbound, 0);
  const storage = toNumberSafe(fees.storage, 0);
  const packaging = toNumberSafe(fees.packaging, 0);
  const packagingLabel = toNumberSafe(fees.packagingLabel, 0);
  const shipping = toNumberSafe(fees.shipping, 0);
  const returnFeePerReturnedProduct = toNumberSafe(fees.returnFee, 9);

  const bundleSkus = Math.max(1, Math.round(toNumberSafe(fees.bundleSkus, 1)));
  const piecesPerSku = Math.max(1, Math.round(toNumberSafe(fees.piecesPerSku, 1)));
  const itemsPerBundle = bundleSkus * piecesPerSku;

  const specialPackagingEnabled = Boolean(fees.specialPackaging);
  const ageVerificationEnabled = Boolean(fees.ageVerification);
  const monthlyQuantity = Math.max(0, Math.round(toNumberSafe(product.estimatedSales, 0)));

  const actualGrossPrice = priceMode === 'net'
    ? grossPrice * (1 + vatPct / 100)
    : grossPrice;

  const marketplaceFee = actualGrossPrice * (marketplacePct / 100);
  const vatAmount = (priceMode === 'gross')
    ? grossPrice - (grossPrice / (1 + vatPct / 100))
    : 0;
  const brokerFee = actualGrossPrice * (brokerPct / 100);
  const advertisingCost = actualGrossPrice * (advertisingPct / 100);

  const pickCostSku = pickPerSku * bundleSkus;
  const pickCostPieces = pickPerPiece * itemsPerBundle;
  const inboundCost = inbound * itemsPerBundle;
  const storageCost = storage * itemsPerBundle;

  let returnCostPerUnit = 0;
  if (monthlyQuantity > 0) {
    const returnedCount = monthlyQuantity * (returnsPct / 100);
    const totalReturnCost = returnedCount * returnFeePerReturnedProduct;
    returnCostPerUnit = Number((totalReturnCost / monthlyQuantity).toFixed(6));
  } else {
    returnCostPerUnit = Number(((returnsPct / 100) * returnFeePerReturnedProduct).toFixed(6));
  }

  const specialPackagingPerUnit = specialPackagingEnabled ? Number((0.5 * itemsPerBundle).toFixed(2)) : 0;
  const ageVerificationPerUnit = ageVerificationEnabled ? 1.67 : 0;

  const perUnitFulfillmentBase = Number((
    orderFee + pickCostSku + pickCostPieces + inboundCost + storageCost +
    packaging + packagingLabel + shipping + returnCostPerUnit +
    specialPackagingPerUnit + ageVerificationPerUnit
  ).toFixed(6));

  const perUnitFulfillment = (fees && fees.fulfillmentOff) 
    ? toNumberSafe(fees.manualFulfillment, 0) 
    : perUnitFulfillmentBase;

  const cogs = toNumberSafe(product.cogs, 0);
  const totalCosts = marketplaceFee + vatAmount + brokerFee + advertisingCost + perUnitFulfillment + cogs;
  const netRevenue = grossPrice - totalCosts;
  const profitMargin = grossPrice > 0 ? (netRevenue / grossPrice) * 100 : 0;

  return {
    grossPrice, marketplaceFee, vatAmount, brokerFee, advertisingCost,
    pickCostSku, pickCostPieces, inboundCost, storageCost,
    returnCostPerUnit, specialPackagingPerUnit, ageVerificationPerUnit,
    perUnitFulfillment, totalCosts, netRevenue, profitMargin,
    isProfit: netRevenue > 0,
    monthlyRevenue: grossPrice * (product.estimatedSales || 0),
    monthlyCosts: totalCosts * (product.estimatedSales || 0),
    monthlyProfit: netRevenue * (product.estimatedSales || 0),
    itemsPerBundle, packaging, cogs, perUnitFulfillmentBase
  };
}

function computeVendorComparison(vendorNetPrice, vendorFees, grossMarketPrice, advertisingPct, cogs) {
  if (!vendorNetPrice || vendorNetPrice <= 0) return null;
  
  const vPrice = toNumberSafe(vendorNetPrice, 0);
  const gPrice = toNumberSafe(grossMarketPrice, 0);
  
  const rueckverguetung = vPrice * (toNumberSafe(vendorFees.rueckverguetung, 0) / 100);
  const marketingCoop = vPrice * (toNumberSafe(vendorFees.marketingCoop, 0) / 100);
  const chargebacks = vPrice * (toNumberSafe(vendorFees.chargebacks, 0) / 100);
  const agenturFee = vPrice * (toNumberSafe(vendorFees.agenturFee, 0) / 100);
  const vendorManager = vPrice * (toNumberSafe(vendorFees.vendorManager, 0) / 100);
  const freightAllowance = vPrice * (toNumberSafe(vendorFees.freightAllowance, 0) / 100);
  const retourenAllowance = vPrice * (toNumberSafe(vendorFees.retourenAllowance, 0) / 100);
  const damageAllowance = vPrice * (toNumberSafe(vendorFees.damageAllowance, 0) / 100);
  const advertising = gPrice * (toNumberSafe(advertisingPct, 0) / 100);
  const cogsAmount = toNumberSafe(cogs, 0);

  const totalCosts = rueckverguetung + marketingCoop + chargebacks + advertising + 
                    agenturFee + vendorManager + freightAllowance + retourenAllowance + 
                    damageAllowance + cogsAmount;

  const netProfit = vPrice - totalCosts;
  const profitMargin = vPrice > 0 ? (netProfit / vPrice) * 100 : 0;
  
  return {
    vendorNetPrice: vPrice,
    rueckverguetung, marketingCoop, chargebacks, advertising,
    agenturFee, vendorManager, freightAllowance, retourenAllowance,
    damageAllowance, cogs: cogsAmount, totalCosts, netProfit, profitMargin
  };
}

/* ========== Components ========== */
const SortedTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const items = [...payload].map(p => ({
    name: p.name || p.dataKey,
    value: p.value,
    color: p.color || p.stroke || p.fill || '#fff'
  }));
  items.sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="custom-tooltip" style={{ background: '#343a40', padding: 10, borderRadius: 6, color: '#fff', border: '1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>{label}</div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, background: it.color, display: 'inline-block', borderRadius: 3 }} />
            <div style={{ fontSize: 13 }}>{it.name}</div>
          </div>
          <div style={{ fontSize: 13 }}>{formatEuNoDecimals(it.value)}</div>
        </div>
      ))}
    </div>
  );
};

/* ========== Main App Component ========== */
function App() {
  // Product state
  const [productData, setProductData] = useState(null);
  const [manualProduct, setManualProduct] = useState({ price: '', estimatedSales: '1', cogs: '' });
  const [priceMode, setPriceMode] = useState('net');

  // Fees state
  const [fees, setFees] = useState({
    marketplace: 15, vat: 19, broker: 11, advertising: 0, returns: 0,
    orderFee: 0.8, pickPerSku: 0.26, pickPerPiece: 0.08, inbound: 0.10, storage: 0.10,
    packaging: 0.20, packagingLabel: 0.20,
    shipping: DEFAULT_SHIPPING_OPTIONS[0].value,
    shippingOptions: DEFAULT_SHIPPING_OPTIONS.map(o => ({ ...o, packagingPrice: 0.40 })),
    packagingSizes: [0.40, 0.85, 1.05],
    specialPackaging: false, ageVerification: false,
    bundleSkus: '1', piecesPerSku: '1',
    country: 'DE',
    returnFee: 9,
    fulfillmentOff: false, manualFulfillment: 0
  });

  // UI state
  const [calculation, setCalculation] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [positionName, setPositionName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [productFocused, setProductFocused] = useState(false);
  const [portfolioFocused, setPortfolioFocused] = useState(false);
  const [feesFocused, setFeesFocused] = useState(false);
  const [portfolioJustUpdated, setPortfolioJustUpdated] = useState(false);

  // Vendor comparison state
  const [vendorPrice, setVendorPrice] = useState('');
  const [vendorFees, setVendorFees] = useState({
    rueckverguetung: 1, marketingCoop: 1, chargebacks: 1, agenturFee: 1,
    vendorManager: 0, freightAllowance: 0, retourenAllowance: 1, damageAllowance: 1,
  });
  const [vendorCalculation, setVendorCalculation] = useState(null);


  // Build shipping options for a given country
  const buildShippingOptionsForCountry = (countryCode) => {
    const defaults = COUNTRY_RATES[countryCode] || {};
    const shippingSizes = Array.isArray(defaults.shippingSizes) && defaults.shippingSizes.length === SHIPPING_SIZE_LABELS.length 
      ? defaults.shippingSizes 
      : DEFAULT_SHIPPING_OPTIONS.map(o => o.value);
    const packagingSizes = Array.isArray(defaults.packagingSizes) && defaults.packagingSizes.length === SHIPPING_SIZE_LABELS.length 
      ? defaults.packagingSizes 
      : [0.40, 0.85, 1.05];

    return shippingSizes.map((sVal, idx) => ({
      label: SHIPPING_SIZE_LABELS[idx].label,
      value: sVal,
      packagingPrice: packagingSizes[idx]
    }));
  };

  // Initialize fees from default country
  useEffect(() => {
    const options = buildShippingOptionsForCountry(fees.country);
    setFees(prev => ({
      ...prev,
      shippingOptions: options,
      shipping: options[0].value,
      packaging: options[0].packagingPrice,
      packagingSizes: (COUNTRY_RATES[prev.country] && COUNTRY_RATES[prev.country].packagingSizes) || prev.packagingSizes
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update calculation when product/fees change
  useEffect(() => {
    if (!productData) return;
    const calc = computeCalculation(productData, fees, priceMode);
    setCalculation(calc);
  }, [fees, productData, priceMode]);

  // Update product data when manual input changes
  useEffect(() => {
    if (manualProduct.price !== '') {
      const product = {
        price: parseFloat(manualProduct.price) || 0,
        estimatedSales: parseInt(manualProduct.estimatedSales || '0', 10) || 0,
        cogs: parseFloat(manualProduct.cogs) || 0
      };
      setProductData(product);
    }
  }, [manualProduct]);

  // Auto-switch focus when all product fields are filled
  useEffect(() => {
    const allFilled = manualProduct.price !== '' && manualProduct.estimatedSales !== '' && manualProduct.cogs !== '';
    if (allFilled) {
      setFeesFocused(true);
      setProductFocused(false);
      setPortfolioFocused(false);
      setPortfolioJustUpdated(false);
    }
  }, [manualProduct.price, manualProduct.estimatedSales, manualProduct.cogs]);

  // Vendor calculation effect
  useEffect(() => {
    if (vendorPrice && vendorPrice !== '') {
      const inputPrice = parseFloat(manualProduct.price) || 0;
      const actualGrossPrice = priceMode === 'net'
        ? inputPrice * (1 + fees.vat / 100)
        : inputPrice;
      
      const vCalc = computeVendorComparison(
        parseFloat(vendorPrice),
        vendorFees,
        actualGrossPrice,
        fees.advertising,
        parseFloat(manualProduct.cogs) || 0
      );
      
      let productNetMargin = 0;
      if (calculation && calculation.grossPrice > 0) {
        productNetMargin = priceMode === 'gross'
          ? ((calculation.netRevenue + calculation.vatAmount) / calculation.grossPrice) * 100
          : (calculation.netRevenue / calculation.grossPrice) * 100;
      }
      
      setVendorCalculation({ ...vCalc, productNetMargin });
    } else {
      setVendorCalculation(null);
    }
  }, [vendorPrice, vendorFees, manualProduct.price, manualProduct.cogs, fees.advertising, fees.vat, priceMode, calculation]);

  // Handlers
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && manualProduct.price) {
      const product = {
        price: parseFloat(manualProduct.price) || 0,
        estimatedSales: parseInt(manualProduct.estimatedSales || '0', 10) || 0,
        cogs: parseFloat(manualProduct.cogs) || 0
      };
      setProductData(product);
      setCalculation(computeCalculation(product, fees, priceMode));
    }
  };

  const handleCountryChange = (countryCode) => {
    const defaults = COUNTRY_RATES[countryCode] || {};
    const shippingOptions = buildShippingOptionsForCountry(countryCode);
    const shippingValue = shippingOptions.length ? shippingOptions[0].value : DEFAULT_SHIPPING_OPTIONS[0].value;
    const packagingSizes = Array.isArray(defaults.packagingSizes) && defaults.packagingSizes.length === SHIPPING_SIZE_LABELS.length 
      ? defaults.packagingSizes 
      : [0.40, 0.85, 1.05];

    setFees(prev => ({
      ...prev,
      country: countryCode,
      orderFee: toNumberSafe(defaults.orderFee ?? prev.orderFee),
      pickPerSku: toNumberSafe(defaults.pickPerSku ?? prev.pickPerSku),
      pickPerPiece: toNumberSafe(defaults.pickPerPiece ?? prev.pickPerPiece),
      inbound: toNumberSafe(defaults.inbound ?? prev.inbound),
      storage: toNumberSafe(defaults.storage ?? prev.storage),
      packagingLabel: toNumberSafe(defaults.packagingLabel ?? prev.packagingLabel),
      shippingOptions,
      shipping: toNumberSafe(shippingValue),
      packagingSizes,
      packaging: shippingOptions[0].packagingPrice
    }));
  };

  const shippingOptionForValue = (val, options) => 
    (options || fees.shippingOptions || DEFAULT_SHIPPING_OPTIONS).find(o => Math.abs(o.value - val) < 1e-9);

  const onShippingChange = (val) => {
    const parsed = parseFloat(val);
    const option = shippingOptionForValue(parsed, fees.shippingOptions);
    setFees(prev => ({
      ...prev,
      shipping: parsed,
      packaging: option ? option.packagingPrice : prev.packaging
    }));
  };

  // Portfolio functions
  const addToPortfolio = () => {
    const estimatedSales = Number(manualProduct.estimatedSales) || 0;
    let calc = calculation;
    
    if (!calc && manualProduct.price) {
      const product = {
        price: parseFloat(manualProduct.price) || 0,
        estimatedSales,
        cogs: parseFloat(manualProduct.cogs) || 0
      };
      calc = computeCalculation(product, fees, priceMode);
    }
    
    if (!calc) return;

    const snapshot = {
      id: crypto.randomUUID(),
      name: (positionName || '').trim() || 'Product',
      qty: 1,
      price: calc.grossPrice,
      monthlyQuantity: estimatedSales,
      monthlyRevenue: calc.monthlyRevenue,
      monthlyProfit: calc.monthlyProfit,
      monthlyCosts: calc.monthlyCosts,
      marketplaceFee: calc.marketplaceFee,
      vatAmount: calc.vatAmount,
      brokerFee: calc.brokerFee,
      advertisingCost: calc.advertisingCost,
      fulfillment: calc.perUnitFulfillment,
      unitCost: calc.totalCosts,
      unitProfit: calc.netRevenue,
      profitMargin: calc.profitMargin,
      returnCostPerUnit: calc.returnCostPerUnit,
      specialPackagingPerUnit: calc.specialPackagingPerUnit,
      ageVerificationPerUnit: calc.ageVerificationPerUnit,
      packaging: calc.packaging,
      country: fees.country,
      cogs: calc.cogs,
      bundleSkus: Number(fees.bundleSkus) || 1,
      piecesPerSku: Number(fees.piecesPerSku) || 1,
      priceMode: priceMode,
      has1P: !!vendorCalculation,
      vendor1PPrice: vendorCalculation ? vendorCalculation.vendorNetPrice : null,
      vendor1PProfit: vendorCalculation ? vendorCalculation.netProfit : null,
      vendor1PMonthlyRevenue: vendorCalculation ? vendorCalculation.vendorNetPrice * estimatedSales : null,
      vendor1PMonthlyProfit: vendorCalculation ? vendorCalculation.netProfit * estimatedSales : null
    };
    
    setPortfolio(prev => [snapshot, ...prev]);
    setPositionName('');
    setPortfolioFocused(false);
  };

  const removeFromPortfolio = (idOrName) => 
    setPortfolio(prev => prev.filter(i => i.id !== idOrName && i.name !== idOrName));

  const updateQty = (nameOrId, qty) => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    setPortfolio(prev => prev.map(i => 
      (i.id === nameOrId || i.name === nameOrId) ? { ...i, qty: q } : i
    ));
    setPortfolioJustUpdated(true);
    setPortfolioFocused(false);
    setFeesFocused(false);
    setProductFocused(false);
  };

  const startEdit = (nameOrId) => {
    const item = portfolio.find(p => p.name === nameOrId || p.id === nameOrId);
    if (!item) return;
    setEditingId(item.name || item.id);
    setPositionName(item.name || '');
    setManualProduct({ price: String(item.price || ''), estimatedSales: String(item.monthlyQuantity || 0), cogs: '' });
    const product = { price: Number(item.price), estimatedSales: Number(item.monthlyQuantity) };
    setProductData(product);
    setCalculation(computeCalculation(product, fees, priceMode));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveEdits = () => {
    if (!editingId) return;
    const prod = {
      price: parseFloat(manualProduct.price) || 0,
      estimatedSales: parseInt(manualProduct.estimatedSales || '0', 10) || 0,
      cogs: parseFloat(manualProduct.cogs) || 0
    };
    const calc = computeCalculation(prod, fees, priceMode);
    
    setPortfolio(prev => prev.map(i => (i.name === editingId || i.id === editingId) ? {
      ...i,
      name: (positionName || '').trim() || 'Product',
      price: calc.grossPrice,
      monthlyQuantity: prod.estimatedSales,
      monthlyRevenue: calc.monthlyRevenue,
      monthlyProfit: calc.monthlyProfit,
      monthlyCosts: calc.monthlyCosts,
      marketplaceFee: calc.marketplaceFee,
      vatAmount: calc.vatAmount,
      brokerFee: calc.brokerFee,
      advertisingCost: calc.advertisingCost,
      fulfillment: calc.perUnitFulfillment,
      unitCost: calc.totalCosts,
      unitProfit: calc.netRevenue,
      profitMargin: calc.profitMargin,
      returnCostPerUnit: calc.returnCostPerUnit,
      specialPackagingPerUnit: calc.specialPackagingPerUnit,
      ageVerificationPerUnit: calc.ageVerificationPerUnit,
      packaging: calc.packaging,
      cogs: calc.cogs,
      bundleSkus: Number(fees.bundleSkus) || i.bundleSkus || 1,
      piecesPerSku: Number(fees.piecesPerSku) || i.piecesPerSku || 1,
      vendor1PMonthlyRevenue: i.has1P ? (i.vendor1PPrice || 0) * prod.estimatedSales : null,
      vendor1PMonthlyProfit: i.has1P ? (i.vendor1PProfit || 0) * prod.estimatedSales : null
    } : i));
    
    setEditingId(null);
    setPositionName('');
    setManualProduct({ price: '', estimatedSales: '', cogs: '' });
    setProductData(null);
    setCalculation(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPositionName('');
    setManualProduct({ price: '', estimatedSales: '', cogs: '' });
    setProductData(null);
    setCalculation(null);
  };

  // Export function
  const performExport = async () => {
    try {
      let mod;
      try {
        mod = await import('xlsx');
      } catch (err) {
        console.error('xlsx import failed', err);
        alert('XLSX-Export benötigt das Paket `xlsx`. Installiere es mit `npm i xlsx` (oder yarn add xlsx) und lade die Seite neu.');
        return;
      }
      const XLSX = (mod && mod.default) ? mod.default : mod;
      const wb = XLSX.utils.book_new();

      if (!portfolio || portfolio.length === 0) {
        alert('Portfolio ist leer — es gibt nichts zu exportieren.');
        return;
      }

      const headerRow = [
        'Name', 'Varianten', 'Price (EUR)', 'Marketplace Fee (EUR)', 'VAT Amount (EUR)',
        'grow Fee (EUR)', 'Advertising Cost (EUR)', 'Fulfillment / Fulfillment cost (EUR)',
        'Unit Cost (EUR)', 'Unit Profit (EUR)', 'Profit Margin (%)', 'Country',
        'SKUs per Bundle', 'Pieces per SKU', 'Estimated monthly qty',
        'Monthly Revenue (EUR)', 'Monthly Cost (EUR)', 'Monthly Profit (EUR)',
        '1P Vendor Price (EUR)', '1P Unit Profit (EUR)', '1P Monthly Revenue (EUR)',
        '1P Monthly Profit (EUR)', '1P vs 3P Diff (EUR)', 'Winner'
      ];

      const rows = portfolio.map(item => {
        const name = (item.name && String(item.name).trim()) ? String(item.name).trim() : 'Product';
        const marginValue = (typeof item.profitMargin === 'number') ? (item.profitMargin / 100) : (Number(item.profitMargin || 0) / 100);
        const qty = item.qty || 1;
        const has1P = item.has1P || false;
        const vendor1PPrice = has1P ? (item.vendor1PPrice || 0) : '';
        const vendor1PProfit = has1P ? (item.vendor1PProfit || 0) : '';
        const vendor1PMonthlyRev = has1P ? (item.vendor1PMonthlyRevenue || 0) * qty : '';
        const vendor1PMonthlyProfit = has1P ? (item.vendor1PMonthlyProfit || 0) * qty : '';
        
        let diff = '';
        let winner = '';
        if (has1P) {
          const profit3p = item.unitProfit || 0;
          const profit1p = item.vendor1PProfit || 0;
          diff = profit1p - profit3p;
          winner = profit1p > profit3p ? '1P' : profit3p > profit1p ? '3P' : 'Equal';
        }

        return [
          name, qty,
          typeof item.price === 'number' ? item.price : (Number(String(item.price || '').replace(',', '.')) || 0),
          item.marketplaceFee ?? 0, item.vatAmount ?? 0, item.brokerFee ?? 0,
          item.advertisingCost ?? 0, item.fulfillment ?? 0, item.unitCost ?? 0,
          item.unitProfit ?? 0, marginValue, item.country ?? '',
          item.bundleSkus ?? (Number(fees.bundleSkus) || 1),
          item.piecesPerSku ?? (Number(fees.piecesPerSku) || 1),
          item.monthlyQuantity ?? 0,
          (item.monthlyRevenue ?? 0) * qty,
          (item.monthlyCosts ?? 0) * qty,
          (item.monthlyProfit ?? 0) * qty,
          vendor1PPrice, vendor1PProfit, vendor1PMonthlyRev, vendor1PMonthlyProfit,
          diff, winner
        ];
      });

      const aoa = [headerRow, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      const currencyCols = [2, 3, 4, 5, 6, 7, 8, 9, 15, 16, 17, 18, 19, 20, 21, 22];
      const percentCol = 10;

      for (let r = 1; r < aoa.length; r++) {
        currencyCols.forEach(cIdx => {
          const addr = XLSX.utils.encode_cell({ r, c: cIdx });
          const cell = ws[addr];
          if (!cell || cell.v === '' || cell.v === null) return;
          if (typeof cell.v === 'string') {
            const maybe = Number(String(cell.v).replace(/\./g, '').replace(',', '.'));
            if (!Number.isNaN(maybe)) cell.v = maybe;
          }
          cell.t = 'n';
          cell.z = '€#,##0.00';
          cell.s = cell.s || {};
          cell.s.font = { name: 'Red Hat Display', sz: 9 };
        });

        const addr = XLSX.utils.encode_cell({ r, c: percentCol });
        const cell = ws[addr];
        if (cell) {
          if (typeof cell.v === 'number' && cell.v > 1) cell.v = cell.v / 100;
          cell.t = 'n';
          cell.z = '0.00%';
          cell.s = cell.s || {};
          cell.s.font = { name: 'Red Hat Display', sz: 9 };
        }

        const qtyAddr = XLSX.utils.encode_cell({ r, c: 1 });
        if (ws[qtyAddr]) {
          ws[qtyAddr].t = 'n';
          ws[qtyAddr].s = ws[qtyAddr].s || {};
          ws[qtyAddr].s.font = { name: 'Red Hat Display', sz: 9 };
        }
      }

      for (let c = 0; c < headerRow.length; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        const cell = ws[addr];
        if (cell) {
          cell.s = cell.s || {};
          cell.s.font = { name: 'Red Hat Display', sz: 9, bold: true };
        }
      }

      ws['!cols'] = headerRow.map((h, i) => {
        if (i === 0) return { wch: 25 };
        if (i === 23) return { wch: 8 };
        if (h.includes('Monthly')) return { wch: 18 };
        return { wch: 14 };
      });

      XLSX.utils.book_append_sheet(wb, ws, 'Portfolio');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portfolio_export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export failed', err);
      alert('Export fehlgeschlagen: ' + (err && err.message ? err.message : err));
    }
  };

  const exportData = () => performExport();

  // Computed values
  const combinedTotals = React.useMemo(() => {
    return portfolio.reduce((acc, cur) => {
      acc.monthlyRevenue += (cur.monthlyRevenue || 0) * (cur.qty || 1);
      acc.monthlyProfit += (cur.monthlyProfit || 0) * (cur.qty || 1);
      return acc;
    }, { monthlyRevenue: 0, monthlyProfit: 0 });
  }, [portfolio]);

  const combinedCosts = combinedTotals.monthlyRevenue - combinedTotals.monthlyProfit;

  const combined1PTotals = portfolio.reduce((acc, cur) => {
    if (cur.has1P) {
      acc.monthlyRevenue += (cur.vendor1PMonthlyRevenue || 0) * (cur.qty || 1);
      acc.monthlyProfit += (cur.vendor1PMonthlyProfit || 0) * (cur.qty || 1);
    }
    return acc;
  }, { monthlyRevenue: 0, monthlyProfit: 0 });

  const portfolioProjection = React.useMemo(() => {
    const mr3p = combinedTotals.monthlyRevenue || 0;
    const mp3p = combinedTotals.monthlyProfit || 0;
    
    const totals1P = portfolio.reduce((acc, cur) => {
      if (cur.has1P) {
        acc.monthlyRevenue += (cur.vendor1PMonthlyRevenue || 0) * (cur.qty || 1);
        acc.monthlyProfit += (cur.vendor1PMonthlyProfit || 0) * (cur.qty || 1);
      }
      return acc;
    }, { monthlyRevenue: 0, monthlyProfit: 0 });
    
    const mr1p = totals1P.monthlyRevenue;
    const mp1p = totals1P.monthlyProfit;
    const has1PData = portfolio.some(p => p.has1P);
    
    const arr = [];
    let cumR3p = 0, cumP3p = 0, cumR1p = 0, cumP1p = 0;
    
    for (let q = 1; q <= 4; q++) {
      cumR3p += mr3p * 3;
      cumP3p += mp3p * 3;
      cumR1p += mr1p * 3;
      cumP1p += mp1p * 3;
      
      arr.push({
        quarter: `Q${q}`,
        revenue3p: Math.round(cumR3p * 100) / 100,
        profit3p: Math.round(cumP3p * 100) / 100,
        revenue1p: has1PData ? Math.round(cumR1p * 100) / 100 : null,
        profit1p: has1PData ? Math.round(cumP1p * 100) / 100 : null
      });
    }
    return arr;
  }, [combinedTotals, portfolio]);

  // Render
  return (
    <div className="app-container">
      <div className="app-inner">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="header">
            <div className="header-left">
              <a href="https://grow.mp" target="_blank" rel="noopener noreferrer" className="logo-link" aria-label="Open grow website">
                <img src={logo} className="logo" alt="grow logo" />
              </a>
              <h1 className="title">3P Marketplace Calculator</h1>
            </div>
            <div className="header-right">
              <a className="book-btn" href="https://calendar.app.google/WAKQbcD6bsmCaW5A9" target="_blank" rel="noopener noreferrer">
                Get Help
              </a>
            </div>
          </div>

          <div className="main-grid">
            {/* Left Column - Product & Fees */}
            <div className="left-column">
              {/* Product Card */}
              <div 
                className={`card ${((!manualProduct.price || !manualProduct.estimatedSales || !manualProduct.cogs) || productFocused) && !portfolioJustUpdated ? 'step-active' : 'step-inactive'}`} 
                onMouseDown={() => { setProductFocused(true); setFeesFocused(false); setPortfolioFocused(false); setPortfolioJustUpdated(false); }}
              >
                <div className="card-header-with-info">
                  <div>
                    <div className="card-title">Product</div>
                    <div className="card-subtitle">Enter price & estimated monthly sales</div>
                  </div>
                  <div className="info-icon" title={INFO_TEXTS.product} onClick={() => window.alert(INFO_TEXTS.product)}>
                    <span>i</span>
                  </div>
                </div>
                
                <div className="form-section">
                  <div>
                    <label className="input-label">Sales Price</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '0.375rem' }}>
                      <input 
                        type="number" 
                        value={manualProduct.price} 
                        onChange={(e) => setManualProduct({ ...manualProduct, price: e.target.value })} 
                        onKeyDown={handleKeyDown} 
                        onFocus={() => setProductFocused(true)} 
                        onBlur={() => setProductFocused(false)} 
                        placeholder="0.00" 
                        className="input-field" 
                        style={{ maxWidth: '140px', marginTop: 0 }}
                      />
                      <div className="price-mode-toggle">
                        <button className={`price-mode-btn ${priceMode === 'net' ? 'active' : ''}`} onClick={() => setPriceMode('net')} type="button">Net</button>
                        <button className={`price-mode-btn ${priceMode === 'gross' ? 'active' : ''}`} onClick={() => setPriceMode('gross')} type="button">Gross</button>
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                      {priceMode === 'net' 
                        ? `Price incl. VAT: ${formatCurrency((parseFloat(manualProduct.price) || 0) * (1 + fees.vat / 100))}`
                        : `Price excl. VAT: ${formatCurrency((parseFloat(manualProduct.price) || 0) / (1 + fees.vat / 100))}`
                      }
                    </div>
                  </div>

                  <div>
                    <label className="input-label">Estimated sales a month (units)</label>
                    <input 
                      type="number" 
                      value={manualProduct.estimatedSales} 
                      onChange={(e) => setManualProduct({ ...manualProduct, estimatedSales: e.target.value })} 
                      onKeyDown={handleKeyDown} 
                      onFocus={() => { setProductFocused(true); setPortfolioJustUpdated(false); }} 
                      onBlur={() => setProductFocused(false)} 
                      placeholder="0" 
                      className="input-field" 
                    />
                  </div>

                  <div>
                    <label className="input-label">COGS (Cost of Goods Sold) per unit</label>
                    <input 
                      type="number" 
                      value={manualProduct.cogs} 
                      onChange={(e) => setManualProduct({ ...manualProduct, cogs: e.target.value })} 
                      onKeyDown={handleKeyDown} 
                      onFocus={() => { setProductFocused(true); setPortfolioJustUpdated(false); }} 
                      onBlur={() => setProductFocused(false)} 
                      placeholder="0.00" 
                      className="input-field" 
                    />
                  </div>

                  {editingId && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={saveEdits} className="save-btn">Save changes</button>
                      <button onClick={cancelEdit} className="reset-btn">Cancel</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Fees Card */}
              <div 
                className={`card ${(manualProduct.price && manualProduct.estimatedSales && manualProduct.cogs && !productFocused && !portfolioFocused && !portfolioJustUpdated) || (feesFocused && !productFocused) ? 'step-active' : 'step-inactive'}`} 
                onMouseDown={() => { setFeesFocused(true); setProductFocused(false); setPortfolioFocused(false); setPortfolioJustUpdated(false); }}
              >
                <div className="card-header-with-info">
                  <div>
                    <div className="card-title">Fee Settings</div>
                    <div className="card-subtitle">Adjust the main fees</div>
                  </div>
                  <div className="info-icon" title={INFO_TEXTS.fees} onClick={() => window.alert(INFO_TEXTS.fees)}>
                    <span>i</span>
                  </div>
                </div>

                <div className="fees-section">
                  <div className="fees-main-grid">
                    <div className="fees-col">
                      <div className="fee-item">
                        <label className="fee-label">Marketplace</label>
                        <select value={fees.marketplace} onChange={(e) => setFees({ ...fees, marketplace: parseInt(e.target.value, 10) })} className="fee-select full">
                          {range(5, 22).map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                      <div className="fee-item">
                        <label className="fee-label">VAT</label>
                        <select value={fees.vat} onChange={(e) => setFees({ ...fees, vat: parseInt(e.target.value, 10) })} className="fee-select full">
                          {range(5, 25).map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                      <div className="fee-item">
                        <label className="fee-label">grow Fee</label>
                        <select value={fees.broker} onChange={(e) => setFees({ ...fees, broker: parseInt(e.target.value, 10) })} className="fee-select full">
                          {range(0, 15).map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="fees-col">
                      <div className="fee-item">
                        <label className="fee-label">Advertising</label>
                        <select value={fees.advertising} onChange={(e) => setFees({ ...fees, advertising: parseInt(e.target.value, 10) })} className="fee-select full">
                          {ADVERTISING_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                      <div className="fee-item">
                        <label className="fee-label">Returns</label>
                        <select value={fees.returns} onChange={(e) => setFees({ ...fees, returns: parseFloat(e.target.value) })} className="fee-select full">
                          {RETURNS_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                      <div className="fee-item">
                        <label className="fee-label">Country</label>
                        <select value={fees.country} onChange={(e) => handleCountryChange(e.target.value)} className="fee-select full country-select">
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bundle-toggle-grid">
                    <div className="bundle-left">
                      <div className="bundle-group-title">Bundle Sets</div>
                      <div className="bundle-field-row">
                        <div className="bundle-field-mini">
                          <label className="bundle-label-mini"># SKUs / bundle</label>
                          <input type="number" value={fees.bundleSkus} onChange={(e) => setFees({ ...fees, bundleSkus: e.target.value })} onBlur={() => setFees(f => ({ ...f, bundleSkus: f.bundleSkus === '' ? '1' : String(Math.max(1, parseInt(f.bundleSkus || '1', 10) || 1)) }))} className="bundle-input-mini" />
                        </div>
                      </div>
                      <div className="bundle-field-row">
                        <div className="bundle-field-mini">
                          <label className="bundle-label-mini"># Pieces / SKU</label>
                          <input type="number" value={fees.piecesPerSku} onChange={(e) => setFees({ ...fees, piecesPerSku: e.target.value })} onBlur={() => setFees(f => ({ ...f, piecesPerSku: f.piecesPerSku === '' ? '1' : String(Math.max(1, parseInt(f.piecesPerSku || '1', 10) || 1)) }))} className="bundle-input-mini" />
                        </div>
                      </div>
                    </div>
                    <div className="bundle-right">
                      <div className="bundle-group-title-right">Packaging</div>
                      <div className="toggle-block inline-row">
                        <label className="toggle-label">Special packaging</label>
                        <div className="toggle-below">
                          <label className="toggle-switch">
                            <input type="checkbox" checked={fees.specialPackaging} onChange={(e) => setFees({ ...fees, specialPackaging: e.target.checked })} />
                            <span className="toggle-track" aria-hidden><span className="toggle-thumb" /></span>
                          </label>
                          <div className="toggle-cost">{formatCurrency(calculation ? calculation.specialPackagingPerUnit : 0)}</div>
                        </div>
                      </div>
                      <div className="toggle-block inline-row">
                        <label className="toggle-label">Age verification</label>
                        <div className="toggle-below">
                          <label className="toggle-switch">
                            <input type="checkbox" checked={fees.ageVerification} onChange={(e) => setFees({ ...fees, ageVerification: e.target.checked })} />
                            <span className="toggle-track" aria-hidden><span className="toggle-thumb" /></span>
                          </label>
                          <div className="toggle-cost">{formatCurrency(calculation ? calculation.ageVerificationPerUnit : 0)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="shipping-grid">
                    <div className="shipping-left">
                      <label className="fee-label">Shipping Label</label>
                      <select value={fees.shipping} onChange={(e) => onShippingChange(e.target.value)} className="shipping-select full">
                        {(fees.shippingOptions || DEFAULT_SHIPPING_OPTIONS).map((o, idx) => (
                          <option key={idx} value={o.value}>{o.label}</option>
                        ))}
                        {(!shippingOptionForValue(fees.shipping, fees.shippingOptions)) && (
                          <option value={fees.shipping}>Country rate: {formatCurrency(fees.shipping)}</option>
                        )}
                      </select>
                    </div>
                    <div className="shipping-right">
                      <div className="fee-label">&nbsp;</div>
                      <div className="shipping-price">{formatCurrency(toNumberSafe(fees.shipping))}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Column - Analysis & Vendor Comparison */}
            <div className="middle-column">
              {/* Profit Analysis Card */}
              <div className={`card ${manualProduct.price && !calculation ? 'step-active' : manualProduct.price ? 'step-completed' : 'step-inactive'}`}>
                <div className="card-header-with-info">
                  <div>
                    <div className="card-title">3P Profit Analysis</div>
                    <div className="card-subtitle">Breakdown & fulfillment</div>
                  </div>
                  <div className="info-icon" title={INFO_TEXTS.analysis} onClick={() => window.alert(INFO_TEXTS.analysis)}>
                    <span>i</span>
                  </div>
                </div>

                {!calculation && (
                  <div className="empty-state">Enter product price and press Calculate to see the breakdown.</div>
                )}

                {calculation && (
                  <div className="analysis-content">
                    <div className="chart-section">
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie 
                            dataKey="value" 
                            data={[
                              { name: 'Marketplace Fee', value: calculation.marketplaceFee },
                              { name: 'VAT', value: calculation.vatAmount },
                              { name: 'Broker Fee', value: calculation.brokerFee },
                              { name: 'Advertising', value: calculation.advertisingCost },
                              { name: 'COGS', value: calculation.cogs },
                              { name: 'Fulfillment', value: calculation.perUnitFulfillment },
                              { name: 'Profit', value: Math.max(0, calculation.netRevenue) }
                            ]} 
                            cx="50%" cy="50%" outerRadius={90} innerRadius={35} labelLine={false}
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, payload }) => {
                              const RAD = Math.PI / 180;
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                              const x = cx + radius * Math.cos(-midAngle * RAD);
                              const y = cy + radius * Math.sin(-midAngle * RAD);
                              const value = payload && typeof payload.value === 'number' ? payload.value : 0;
                              const safePercent = calculation && calculation.grossPrice ? value / calculation.grossPrice : 0;
                              const pct = Math.round(safePercent * 100);
                              if (pct === 0) return null;
                              return <text x={x} y={y} fill="#fff" fontSize={11} fontWeight={600} textAnchor="middle" dominantBaseline="central">{`${pct}%`}</text>;
                            }} 
                            paddingAngle={0} cornerRadius={6}
                          >
                            {CHART_COLORS.map((c, i) => <Cell key={i} fill={i === 6 ? '#52b788' : c} stroke="none" />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>

                      <div className="profit-kpis-row">
                        <div className="profit-kpi">
                          <div className="profit-kpi-label">Net / unit</div>
                          <div className="profit-kpi-value" style={{ color: calculation.netRevenue >= 0 ? '#52b788' : '#ef4444' }}>
                            {formatCurrency(calculation.netRevenue)}
                          </div>
                        </div>
                        <div className="profit-kpi">
                          <div className="profit-kpi-label">Net Margin</div>
                          <div className="profit-kpi-value" style={{ color: calculation.profitMargin >= 0 ? '#52b788' : '#ef4444' }}>
                            {calculation.profitMargin.toFixed(1)}%
                          </div>
                        </div>
                      </div>

                      <div className="legend">
                        {[
                          { name: 'Marketplace Fee', color: CHART_COLORS[0], value: calculation.marketplaceFee },
                          { name: 'VAT', color: CHART_COLORS[1], value: calculation.vatAmount },
                          { name: 'Broker Fee', color: CHART_COLORS[2], value: calculation.brokerFee },
                          { name: 'Advertising', color: CHART_COLORS[3], value: calculation.advertisingCost },
                          { name: 'COGS', color: CHART_COLORS[4], value: calculation.cogs },
                          { name: 'Fulfillment', color: CHART_COLORS[5], value: calculation.perUnitFulfillment }
                        ].map((d, i) => (
                          <div key={i} className="legend-item">
                            <div className="legend-icon" style={{ backgroundColor: d.color }} />
                            <div className="legend-name">{d.name}</div>
                            <div className="legend-value">{formatCurrency(d.value)}</div>
                          </div>
                        ))}
                        <div className="total-cost-kpi">
                          <div className="total-cost-label">Total Costs</div>
                          <div className="total-cost-value">{formatCurrency(calculation.totalCosts)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="fulfillment-column">
                      <div className="fulfillment-card">
                        <div className="fulfillment-header">
                          <div className="fulfillment-title">
                            <div className="fulfillment-text">Fulfillment</div>
                          </div>
                          <div className="fulfillment-actions">
                            <label className={`toggle-switch${fees.fulfillmentOff ? ' fulfillment-toggle' : ''}`} title={fees.fulfillmentOff ? "Fulfillment costs OFF" : "Fulfillment costs ON"}>
                              <input type="checkbox" checked={!fees.fulfillmentOff} onChange={(e) => setFees({ ...fees, fulfillmentOff: !e.target.checked })} />
                              <span className="toggle-track" aria-hidden><span className="toggle-thumb" /></span>
                            </label>
                          </div>
                        </div>

                        <div className="fulfillment-total">
                          Per unit: {fees.fulfillmentOff ? (
                            <input type="number" step="0.01" value={fees.manualFulfillment} onChange={(e) => setFees({ ...fees, manualFulfillment: e.target.value })} className="fulfillment-input fulfillment-input--highlight" />
                          ) : (
                            <strong>{formatCurrency(calculation.perUnitFulfillment)}</strong>
                          )}
                        </div>

                        <AnimatePresence>
                          {!fees.fulfillmentOff && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28 }} className="fulfillment-details-animated">
                              <table className="fulfillment-table">
                                <tbody>
                                  <tr><td>Order Fee</td><td>{formatCurrency(toNumberSafe(fees.orderFee))}</td></tr>
                                  <tr><td>Pick / SKU</td><td>{formatCurrency(toNumberSafe(fees.pickPerSku) * toNumberSafe(fees.bundleSkus || 1))}</td></tr>
                                  <tr><td>Pick / Piece</td><td>{formatCurrency(toNumberSafe(fees.pickPerPiece) * toNumberSafe(fees.bundleSkus || 1) * toNumberSafe(fees.piecesPerSku || 1))}</td></tr>
                                  <tr><td>Inbound</td><td>{formatCurrency(calculation.inboundCost)}</td></tr>
                                  <tr><td>Storage</td><td>{formatCurrency(calculation.storageCost)}</td></tr>
                                  <tr><td>Packaging</td><td>{formatCurrency(calculation.packaging)}</td></tr>
                                  <tr><td>Label</td><td>{formatCurrency(toNumberSafe(fees.packagingLabel))}</td></tr>
                                  <tr><td>Shipping</td><td>{formatCurrency(toNumberSafe(fees.shipping))}</td></tr>
                                  <tr><td>Return ({fees.returns}%)</td><td>{formatCurrency(calculation.returnCostPerUnit)}</td></tr>
                                  {fees.specialPackaging && <tr><td>Special Packaging</td><td>{formatCurrency(calculation.specialPackagingPerUnit)}</td></tr>}
                                  {fees.ageVerification && <tr><td>Age Verification</td><td>{formatCurrency(calculation.ageVerificationPerUnit)}</td></tr>}
                                </tbody>
                              </table>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Vendor Comparison Card */}
              <div className="card vendor-comparison-card">
                <div className="card-header-with-info">
                  <div>
                    <div className="card-title">Compare 1P Amazon Vendor</div>
                    <div className="card-subtitle">Analyze vendor pricing model</div>
                  </div>
                  <div className="info-icon" title={INFO_TEXTS.vendor} onClick={() => window.alert(INFO_TEXTS.vendor)}>
                    <span>i</span>
                  </div>
                </div>

                {!calculation && (
                  <div className="empty-state">Calculate a product first to compare with 1P vendor model</div>
                )}

                {calculation && (
                  <div className="vendor-content">
                    <div className="vendor-top-row">
                      <div className="vendor-left">
                        <div className="vendor-input-section">
                          <label className="input-label">Vendor Net Sales Price</label>
                          <input type="number" value={vendorPrice} onChange={(e) => setVendorPrice(e.target.value)} placeholder="0.00" className="input-field" style={{ maxWidth: '160px', marginTop: '0.375rem' }} />
                        </div>
                      </div>

                      {vendorCalculation && (
                        <div className="vendor-right">
                          <div className="vendor-bar-section">
                            <div className="vendor-bar-label">Profit vs Costs – Per unit</div>
                            <div className="vendor-bar">
                              <div className="vendor-profit-segment" style={{ width: `${Math.max(0, Math.min(100, (vendorCalculation.netProfit / vendorCalculation.vendorNetPrice) * 100))}%`, background: '#52b788' }} />
                              <div className="vendor-cost-segment" style={{ width: `${Math.max(0, Math.min(100, (vendorCalculation.totalCosts / vendorCalculation.vendorNetPrice) * 100))}%`, background: `linear-gradient(90deg, ${CHART_COLORS[0]}, ${CHART_COLORS[5]})` }} />
                            </div>
                            <div className="vendor-bar-footer">
                              <div className="vendor-bar-profit">Profit: {formatCurrency(vendorCalculation.netProfit)}</div>
                              <div className="vendor-bar-cost">Costs: {formatCurrency(vendorCalculation.totalCosts)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {vendorCalculation && (
                      <>
                        <div className="vendor-kpis-row">
                          <div className="vendor-kpi-slim">
                            <div className="vendor-kpi-label">Net / unit</div>
                            <div className="vendor-kpi-value" style={{ color: vendorCalculation.netProfit >= 0 ? '#52b788' : '#ef4444' }}>
                              {formatCurrency(vendorCalculation.netProfit)}
                            </div>
                          </div>
                          <div className="vendor-kpi-slim">
                            <div className="vendor-kpi-label">1P vs 3P %</div>
                            <div className="vendor-kpi-value" style={{ 
                              color: (() => {
                                const p3 = calculation ? calculation.netRevenue : 0;
                                const p1 = vendorCalculation.netProfit;
                                return p1 > p3 ? '#52b788' : p1 < p3 ? '#ef4444' : '#fff';
                              })(),
                              fontSize: '0.9rem'
                            }}>
                              {(() => {
                                const p3 = calculation ? calculation.netRevenue : 0;
                                const p1 = vendorCalculation.netProfit;
                                const diff = p1 - p3;
                                const is1PBetter = diff > 0;
                                
                                // Use loser as basis for percentage
                                const base = is1PBetter ? Math.abs(p3) : Math.abs(p1);
                                
                                if (base === 0) {
                                  return diff > 0 ? '+∞' : diff < 0 ? '-∞' : '0%';
                                }
                                
                                const relativeDiff = (Math.abs(diff) / base) * 100;
                                const sign = is1PBetter ? '+' : '-';
                                return `${sign}${relativeDiff.toFixed(0)}%`;
                              })()}
                            </div>
</div>
                          <div className="vendor-kpi-slim">
                            <div className="vendor-kpi-label">1P vs 3P €</div>
                            <div className="vendor-kpi-value" style={{ color: vendorCalculation.netProfit > calculation.netRevenue ? '#52b788' : '#ef4444', fontSize: '0.9rem' }}>
                              {(() => {
                                const difference = vendorCalculation.netProfit - calculation.netRevenue;
                                return <>{difference > 0 ? '↑' : '↓'}{formatCurrency(Math.abs(difference))}</>;
                              })()}
                            </div>
                          </div>
                        </div>

                        <div className="vendor-fees-grid-compact vendor-fees-8items">
                          <div className="vendor-fee-compact">
                            <label>Refund</label>
                            <select value={vendorFees.rueckverguetung} onChange={(e) => setVendorFees({ ...vendorFees, rueckverguetung: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 20).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Marketing Coop</label>
                            <select value={vendorFees.marketingCoop} onChange={(e) => setVendorFees({ ...vendorFees, marketingCoop: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 20).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Chargebacks</label>
                            <select value={vendorFees.chargebacks} onChange={(e) => setVendorFees({ ...vendorFees, chargebacks: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 15).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Agency Fee</label>
                            <select value={vendorFees.agenturFee} onChange={(e) => setVendorFees({ ...vendorFees, agenturFee: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 10).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Vendor Manager</label>
                            <select value={vendorFees.vendorManager} onChange={(e) => setVendorFees({ ...vendorFees, vendorManager: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 10).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Freight Allow.</label>
                            <select value={vendorFees.freightAllowance} onChange={(e) => setVendorFees({ ...vendorFees, freightAllowance: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 10).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Returns Allow.</label>
                            <select value={vendorFees.retourenAllowance} onChange={(e) => setVendorFees({ ...vendorFees, retourenAllowance: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 10).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                          <div className="vendor-fee-compact">
                            <label>Damage Allow.</label>
                            <select value={vendorFees.damageAllowance} onChange={(e) => setVendorFees({ ...vendorFees, damageAllowance: parseInt(e.target.value) })} className="vendor-fee-select-small">
                              {range(0, 10).map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Portfolio */}
            <div className="right-column">
              {/* Add to Portfolio Card */}
              <div className={`card ${portfolioFocused ? 'step-active' : 'step-inactive'}`} onMouseDown={() => { setPortfolioFocused(true); setProductFocused(false); setFeesFocused(false); setPortfolioJustUpdated(false); }}>
                <div className="card-header-with-info">
                  <div>
                    <div className="card-title">Add to Portfolio</div>
                    <div className="card-subtitle">Save positions with same conditions</div>
                  </div>
                  <div className="info-icon" title={INFO_TEXTS.addPortfolio} onClick={() => window.alert(INFO_TEXTS.addPortfolio)}>
                    <span>i</span>
                  </div>
                </div>

                {vendorCalculation && calculation && (
                  <div className="comparison-chart-wrapper">
                    <div className="comparison-title">Monthly Profit: 3P vs 1P</div>
                    <div className="comparison-bars">
                      <div className="comparison-bar-group">
                        <div className="comparison-bar-label">3P Seller</div>
                        <div className="comparison-bar-track">
                          <div className="comparison-bar-fill bar-3p" style={{ width: `${Math.min(100, Math.max(5, (calculation.monthlyProfit / Math.max(calculation.monthlyProfit, vendorCalculation.netProfit * (parseInt(manualProduct.estimatedSales) || 1))) * 100))}%` }} />
                        </div>
                        <div className="comparison-bar-value">{formatEuNoDecimals(calculation.monthlyProfit)}</div>
                      </div>
                      <div className="comparison-bar-group">
                        <div className="comparison-bar-label">1P Vendor</div>
                        <div className="comparison-bar-track">
                          <div className="comparison-bar-fill bar-1p" style={{ width: `${Math.min(100, Math.max(5, (vendorCalculation.netProfit * (parseInt(manualProduct.estimatedSales) || 1) / Math.max(calculation.monthlyProfit, vendorCalculation.netProfit * (parseInt(manualProduct.estimatedSales) || 1))) * 100))}%` }} />
                        </div>
                        <div className="comparison-bar-value">{formatEuNoDecimals(vendorCalculation.netProfit * (parseInt(manualProduct.estimatedSales) || 1))}</div>
                      </div>
                    </div>
                    <div className="comparison-summary">
                                          {(() => {
                                            const profit3p = calculation.monthlyProfit;
                                            const profit1p = vendorCalculation.netProfit * (parseInt(manualProduct.estimatedSales) || 1);
                                            const diff = profit1p - profit3p;
                                            const is1PBetter = diff > 0;
                                            
                                            // Use loser as basis for percentage calculation
                                            const base = is1PBetter ? Math.abs(profit3p) : Math.abs(profit1p);
                                            const relativeDiff = base !== 0 ? (Math.abs(diff) / base) * 100 : 0;
                                            
                                            if (relativeDiff < 0.5) {
                                              return <span style={{ color: '#9ca3af' }}>Equal profitability</span>;
                                            }
                                            
                                            const winner = is1PBetter ? '1P' : '3P';
                                            const loser = is1PBetter ? '3P' : '1P';
                                            const color = is1PBetter ? '#f59e0b' : '#94a3b8';
                                            
                                            return <span style={{ color }}>{winner} is +{relativeDiff.toFixed(0)}% more profitable than {loser}</span>;
                                          })()}
                                        </div>
                  </div>
                )}

                <div className="form-section" style={{ gap: 12 }}>
                  <div>
                    <label className="input-label">Product Name (optional)</label>
                    <input
                      type="text"
                      value={positionName}
                      onChange={(e) => setPositionName(e.target.value)}
                      maxLength={100}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addToPortfolio();
                          setPortfolioFocused(false);
                          setFeesFocused(false);
                          setProductFocused(false);
                          setPortfolioJustUpdated(true);
                        }
                      }}
                      onFocus={() => setPortfolioFocused(true)}
                      onBlur={() => setPortfolioFocused(false)}
                      placeholder="e.g. Black Mug 350ml"
                      className="input-field"
                    />
                    <div className="input-hint">Press Enter to add to portfolio</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={() => { addToPortfolio(); setPortfolioFocused(false); setFeesFocused(false); setProductFocused(false); setPortfolioJustUpdated(true); }} className="portfolio-btn" aria-label="Add to portfolio">Add</button>
                  </div>
                </div>
              </div>

              {/* Portfolio Products Card */}
              <div className={`card portfolio-flex ${portfolioJustUpdated ? 'step-active' : 'step-inactive'}`} onMouseDown={() => { setPortfolioFocused(true); setProductFocused(false); setFeesFocused(false); setPortfolioJustUpdated(false); }}>
                <div className="card-header-with-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div className="card-title">Portfolio Products</div>
                    <div className="card-subtitle">Positions</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div className="info-icon" title={INFO_TEXTS.portfolio} onClick={() => window.alert(INFO_TEXTS.portfolio)}>
                      <span>i</span>
                    </div>
                    <button onClick={exportData} className="card-action-btn" title="Export portfolio to Excel">Export</button>
                  </div>
                </div>

                {portfolio.length > 0 && (
                  <>
                    <div className="portfolio-chart-wrapper">
                      <div className="portfolio-legend">
                        {portfolio.some(p => p.has1P) ? '3P vs 1P: Revenue & Profit' : 'Cumulative: Revenue | Profit'}
                      </div>
                      <div className="monthly-chart portfolio-line-chart" style={{ height: 140 }}>
                        <ResponsiveContainer width="100%" height={140}>
                          <LineChart data={portfolioProjection} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2b3238" />
                            <XAxis dataKey="quarter" tick={{ fill: '#ffffff', fontSize: 11 }} />
                            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={shortenK} />
                            <Tooltip content={<SortedTooltip />} />
                            <Line type="monotone" dataKey="revenue3p" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} name="3P Revenue" />
                            <Line type="monotone" dataKey="profit3p" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="6 4" name="3P Profit" />
                            {portfolio.some(p => p.has1P) && (
                              <>
                                <Line type="monotone" dataKey="revenue1p" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="1P Revenue" />
                                <Line type="monotone" dataKey="profit1p" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="6 4" name="1P Profit" />
                              </>
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="monthly-kpis-section">
                      <div className="monthly-kpis-header">Monthly KPIs</div>
                      <div className="monthly-kpis-grid">
                        <div className="monthly-kpi-mini">
                          <div className="monthly-kpi-label">3P Revenue</div>
                          <div className="monthly-kpi-value" style={{ color: '#94a3b8' }}>{formatEuNoDecimals(combinedTotals.monthlyRevenue)}</div>
                        </div>
                        <div className="monthly-kpi-mini">
                          <div className="monthly-kpi-label">3P Profit</div>
                          <div className="monthly-kpi-value" style={{ color: combinedTotals.monthlyProfit >= 0 ? '#94a3b8' : '#ef4444' }}>{formatEuNoDecimals(combinedTotals.monthlyProfit)}</div>
                        </div>
                        {portfolio.some(p => p.has1P) ? (
                          <>
                            <div className="monthly-kpi-mini">
                              <div className="monthly-kpi-label">1P Revenue</div>
                              <div className="monthly-kpi-value" style={{ color: '#f59e0b' }}>{formatEuNoDecimals(combined1PTotals.monthlyRevenue)}</div>
                            </div>
                            <div className="monthly-kpi-mini">
                              <div className="monthly-kpi-label">1P Profit</div>
                              <div className="monthly-kpi-value" style={{ color: combined1PTotals.monthlyProfit >= 0 ? '#f59e0b' : '#ef4444' }}>{formatEuNoDecimals(combined1PTotals.monthlyProfit)}</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="monthly-kpi-mini">
                              <div className="monthly-kpi-label">Costs</div>
                              <div className="monthly-kpi-value">{formatEuNoDecimals(combinedCosts)}</div>
                            </div>
                            <div className="monthly-kpi-mini">
                              <div className="monthly-kpi-label">Margin</div>
                              <div className="monthly-kpi-value">{combinedTotals.monthlyRevenue > 0 ? ((combinedTotals.monthlyProfit / combinedTotals.monthlyRevenue) * 100).toFixed(1) : 0}%</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {portfolio.length === 0 && (
                  <div className="empty-state">Your portfolio is empty. Add a position to save it.</div>
                )}

                {portfolio.length > 0 && (
                  <div className="portfolio-list">
                    {portfolio.map(item => (
                      <div key={item.id || item.name} className="portfolio-item-compact">
                        <div className="portfolio-item-header">
                          <div className="portfolio-item-name">{item.name || 'Product'}</div>


                        </div>
                        <div className="portfolio-item-body">
                          <div className="portfolio-item-kpis">
                            <div className="portfolio-kpi">
                              <span className="portfolio-kpi-label">Price</span>
                              <span className="portfolio-kpi-value">{formatEuNoDecimals(item.price)}</span>
                            </div>
                            <div className="portfolio-kpi">
                              <span className="portfolio-kpi-label">Qty/mo</span>
                              <span className="portfolio-kpi-value">{item.monthlyQuantity}</span>
                            </div>
                            <div className="portfolio-kpi">
                              <span className="portfolio-kpi-label">Margin</span>
                              <span className="portfolio-kpi-value">{item.profitMargin ? item.profitMargin.toFixed(1) : '—'}%</span>
                            </div>
                            <div className="portfolio-kpi">
                              <span className="portfolio-kpi-label">Cost</span>
                              <span className="portfolio-kpi-value">{formatEuNoDecimals(item.unitCost)}</span>
                            </div>
                          </div>
                          <div className="portfolio-item-controls">
                            <div className="qty-controls">
                              <button className="qty-btn-sm" onClick={() => { updateQty(item.id || item.name, (item.qty || 1) - 1); setPortfolioJustUpdated(true); }}>−</button>
                              <input className="qty-input-sm" type="number" value={item.qty} onChange={(e) => updateQty(item.id || item.name, e.target.value)} />
                              <button className="qty-btn-sm" onClick={() => { updateQty(item.id || item.name, (item.qty || 1) + 1); setPortfolioJustUpdated(true); }}>+</button>
                            </div>
                            <div className="portfolio-item-actions">
                              <button type="button" className="action-link" onClick={(e) => { e.stopPropagation(); startEdit(item.id || item.name); }}>Edit</button>
                              <button type="button" className="action-link delete" onClick={(e) => { e.stopPropagation(); removeFromPortfolio(item.id || item.name); }}>Delete</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="footer">
            <Analytics />
            <div className="footer-left">© {new Date().getFullYear()} grow. All rights reserved. More infos on www.grow.mp</div>
            <div className="footer-right">Built with 🤖 in Cologne. Free to use for quick pricing checks.</div>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;